import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { useAuthenticationService } from '../services/authentication.service';
import ToastService from '../services/toast.service';
import logger from '../utils/logger';
import { ALL_CENTRE_ROLES } from '../utils/constants/roles';

const PermissionContext = createContext(null);

export function PermissionProvider({ children }) {
	const auth = useAuthenticationService();
	const isAuthenticated = auth.isAuthenticated();
	const hasAllCentreAccess = auth.isInAnyRole(ALL_CENTRE_ROLES);
	const [centres, setCentres] = useState([]);
	const [isLoadingCentres, setIsLoadingCentres] = useState(isAuthenticated);
	const [hasLoadedCentres, setHasLoadedCentres] = useState(false);
	const [centresError, setCentresError] = useState(null);
	const centresLoading = isAuthenticated && (isLoadingCentres || !hasLoadedCentres);

	useEffect(() => {
		let cancelled = false;

		if (!isAuthenticated) {
			setCentres([]);
			setIsLoadingCentres(false);
			setHasLoadedCentres(false);
			setCentresError(null);
			return () => {
				cancelled = true;
			};
		}

		async function loadCentres() {
			setIsLoadingCentres(true);
			setHasLoadedCentres(false);
			setCentresError(null);
			try {
				const response = hasAllCentreAccess
					? await api.getCentres()
					: await api.getMyCentres();
				const data = response?.data ?? response;
				if (!Array.isArray(data)) {
					throw new Error('Invalid centre access response');
				}
				if (!cancelled) setCentres(data);
			} catch (error) {
				if (!cancelled) {
					setCentres([]);
					setCentresError(error);
					logger.error('Error loading authorised centres:', error);
					if (!error?.toastShown) {
						ToastService.error('Unable to load your authorised centres');
					}
				}
			} finally {
				if (!cancelled) {
					setIsLoadingCentres(false);
					setHasLoadedCentres(true);
				}
			}
		}

		loadCentres();
		return () => {
			cancelled = true;
		};
	}, [hasAllCentreAccess, isAuthenticated]);

	const value = useMemo(() => ({
		centres,
		centresLoading,
		centresError,
		hasAllCentreAccess,
		isCentreAuthorised: (centreId) => Boolean(centreId) && (hasAllCentreAccess
			|| centres.some((centre) => String(centre?.centreId) === String(centreId))),
	}), [centres, centresError, centresLoading, hasAllCentreAccess]);

	return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissions() {
	const context = useContext(PermissionContext);
	if (!context) {
		throw new Error('usePermissions must be used within a PermissionProvider');
	}
	return context;
}

export default PermissionContext;
